import { create } from 'zustand';
import {
  ClientSessionParticipant,
  ClientSessionReviewStatus,
  GetClientSessionsDocument,
  GetClientSessionsCountDocument,
  CommonPageInfo,
  ClientSession,
} from '../../../../generated/graphql';
import { Selectable } from '../components/Table';
import { PAGE_SIZE, SESSION_STATES } from '../utils/constants';
import { useTableStore } from './useTableStore';
import { apolloClient } from '../../../../lib/apolloHelpers';

interface SessionsState {
  // State
  currentStatus: ClientSessionReviewStatus;
  selectedSessions: Selectable[];
  expandedGroups: Set<string>;
  currentPage: number;
  pathStatus: typeof SESSION_STATES[keyof typeof SESSION_STATES];
  sessions: ClientSession[];
  pageInfo: CommonPageInfo;
  totalCount: number;
  pendingCount: number;
  confirmedCount: number;
  dismissedCount: number;
  loading: boolean;
  error: Error | null;

  // Actions
  setCurrentStatus: (status: ClientSessionReviewStatus) => void;
  setSelectedSessions: (sessions: Selectable[]) => void;
  clearSelection: () => void;
  toggleGroup: (groupId: string, clients: ClientSessionParticipant[]) => void;
  setNextPage: () => void;
  setPreviousPage: () => void;
  setPathStatus: (pathname: string) => void;

  // Data fetching
  fetchSessions: (suppressLoading?: boolean) => Promise<void>;
  refetchAll: () => Promise<void>;
  fetchAllCounts: () => Promise<void>;
  fetchSessionsWithCount: () => Promise<void>;

  // Utility function to handle common error states
  handleQueryError: (error: unknown) => void;
}

export const useSessionsStore = create<SessionsState>((set, get) => ({
  // Initial state
  currentStatus: ClientSessionReviewStatus.PendingReview,
  selectedSessions: [],
  expandedGroups: new Set<string>(),
  pathStatus: SESSION_STATES.PENDING,
  currentPage: 1,
  sessions: [],
  pageInfo: null,
  totalCount: 0,
  pendingCount: 0,
  confirmedCount: 0,
  dismissedCount: 0,
  loading: false,
  error: null,

  // Actions
  setCurrentStatus: (status) =>
    set((state) => {
      if (state.currentStatus === status) {
        return { currentStatus: status };
      }

      // Clear table selections when status changes
      useTableStore.getState().handleFilterChange();

      return {
        currentStatus: status,
        currentPage: 1,
        selectedSessions: [],
        expandedGroups: new Set(),
      };
    }),

  setSelectedSessions: (sessions) => set({ selectedSessions: sessions }),

  clearSelection: () => set({ selectedSessions: [] }),

  toggleGroup: (groupId, clients) =>
    set((state) => ({
      expandedGroups: state.expandedGroups.has(groupId)
        ? new Set(
            [...Array.from(state.expandedGroups)].filter(
              (id) => id !== groupId,
            ),
          )
        : new Set([...Array.from(state.expandedGroups), groupId]),
    })),

  setNextPage: () => {
    set((state) => ({ currentPage: state.currentPage + 1 }));
    get()
      .fetchSessions()
      .catch(() => {
        /* error already handled in store */
      });
  },

  setPreviousPage: () => {
    set((state) => ({ currentPage: Math.max(state.currentPage - 1, 1) }));
    get()
      .fetchSessions()
      .catch(() => {
        /* error already handled in store */
      });
  },

  setPathStatus: (pathname) =>
    set(() => {
      const newPathStatus =
        Object.values(SESSION_STATES).find((state) =>
          pathname.includes(state.path),
        ) || SESSION_STATES.PENDING;

      const statusMap = {
        [SESSION_STATES.PENDING.path]: ClientSessionReviewStatus.PendingReview,
        [SESSION_STATES.CONFIRMED.path]: ClientSessionReviewStatus.Confirmed,
        [SESSION_STATES.DISMISSED.path]: ClientSessionReviewStatus.Dismissed,
      };

      const newCurrentStatus = statusMap[newPathStatus.path];

      // Clear table selections when path changes
      useTableStore.getState().handleFilterChange();

      return {
        pathStatus: newPathStatus,
        currentStatus: newCurrentStatus,
        currentPage: 1,
      };
    }),

  // Utility function to handle common error states
  handleQueryError: (error: unknown) =>
    set({ error: error as Error, loading: false }),

  // Fetch all status counts in parallel
  fetchAllCounts: async () => {
    try {
      const [pending, confirmed, dismissed] = await Promise.all([
        apolloClient.query({
          query: GetClientSessionsCountDocument,
          variables: {
            input: {
              reviewStatus: ClientSessionReviewStatus.PendingReview,
            },
          },
        }),
        apolloClient.query({
          query: GetClientSessionsCountDocument,
          variables: {
            input: {
              reviewStatus: ClientSessionReviewStatus.Confirmed,
            },
          },
        }),
        apolloClient.query({
          query: GetClientSessionsCountDocument,
          variables: {
            input: {
              reviewStatus: ClientSessionReviewStatus.Dismissed,
            },
          },
        }),
      ]);
      set({
        pendingCount: pending.data.clientSessionsCount,
        confirmedCount: confirmed.data.clientSessionsCount,
        dismissedCount: dismissed.data.clientSessionsCount,
      });
    } catch (error) {
      get().handleQueryError(error);
      throw error;
    }
  },

  // Main sessions fetch
  fetchSessions: async (suppressLoading = false) => {
    const state = get();
    if (!suppressLoading) {
      set({ loading: true, error: null });
    }
    try {
      const { data } = await apolloClient.query({
        query: GetClientSessionsDocument,
        variables: {
          input: {
            filter: { reviewStatus: state.currentStatus },
            page: state.currentPage,
            pageSize: PAGE_SIZE,
          },
        },
      });

      set({
        sessions: data.clientSessions.edges,
        pageInfo: data.clientSessions.pageInfo,
        totalCount: data.clientSessions.totalCount,
        ...(suppressLoading ? {} : { loading: false }),
      });
    } catch (error) {
      get().handleQueryError(error);
      throw error;
    }
  },

  // Combined fetch for both sessions and counts
  fetchSessionsWithCount: async () => {
    const state = get();
    set({ loading: true, error: null });

    try {
      await Promise.all([state.fetchSessions(true), state.fetchAllCounts()]);
      set({ loading: false });
    } catch {
      // Error and loading state already handled by the individual fetch methods
    }
  },

  // DEPRECATED: Replace refetchAll with fetchSessionsWithCount
  refetchAll: async () => {
    await get().fetchSessionsWithCount();
  },
}));
